import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { SubscriptionPlan, CreatePlanForm } from '../../types';

// All available features with their display info
const ALL_FEATURES = [
  { code: 'ENTITY_INJECTION', name: 'Entity Injection', icon: '🚀', category: 'SEO', description: 'SEO titles, slugs, meta for Dhaka' },
  { code: 'SOCIAL_CAPTION', name: 'Social Caption', icon: '📱', category: 'Social', description: 'FB/WhatsApp captions with BDT pricing' },
  { code: 'FCOMMERCE_REPLY', name: 'F-Commerce Reply', icon: '💬', category: 'Social', description: 'Public replies & inbox scripts' },
  { code: 'TRUST_GAP_AUDIT', name: 'Trust Gap Audit', icon: '⚖️', category: 'Audit', description: 'BSTI, Halal verification audit' },
  { code: 'EXPIRY_HELPER', name: 'Expiry Helper', icon: '📅', category: 'Tools', description: 'Product expiry management' },
  { code: 'COMPETITOR_ANALYSIS', name: 'Competitor Analysis', icon: '🔍', category: 'Research', description: 'Competitive intelligence' },
  { code: 'JSONLD_GENERATOR', name: 'JSON-LD Generator', icon: '📊', category: 'SEO', description: 'Structured data generation' },
  { code: 'MAPS_GROUNDING', name: 'Maps Grounding', icon: '📍', category: 'Research', description: 'Google Maps local search' },
  { code: 'AI_ART_PROMPT', name: 'AI Art Prompt', icon: '🎨', category: 'Creative', description: 'Product photography prompts' },
  { code: 'INFOGRAPHIC', name: 'Infographic', icon: '📈', category: 'Creative', description: 'Visual content generation' },
  { code: 'SAAS_FRAMEWORK', name: 'SaaS Framework', icon: '🗝️', category: 'Research', description: 'Product roadmap planning' },
  { code: 'MAP_PCP_CLIENTS', name: 'Map PCP Clients', icon: '🗺️', category: 'Sales', description: 'Client mapping tool' },
  { code: 'MARKET_LINKAGE', name: 'Market Linkage AI', icon: '🌉', category: 'Logistics', description: 'Urban-Rural bridge tool' },
  { code: 'VIDEO_GENERATION', name: 'Video Generation', icon: '🎬', category: 'Creative', description: '30s cinematic video generation' },
  { code: 'API_ACCESS', name: 'API Access', icon: '🔌', category: 'Integration', description: 'Direct API access for integrations' },
  { code: 'PRIORITY_SUPPORT', name: 'Priority Support', icon: '🎯', category: 'Support', description: '24/7 priority support access' },
  { code: 'CUSTOM_BRANDING', name: 'Custom Branding', icon: '🏷️', category: 'Branding', description: 'White-label branding options' },
];

// Group features by category
const FEATURE_CATEGORIES = ALL_FEATURES.reduce((acc, feature) => {
  if (!acc[feature.category]) acc[feature.category] = [];
  acc[feature.category].push(feature);
  return acc;
}, {} as Record<string, typeof ALL_FEATURES>);

const AdminPlans: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [form, setForm] = useState<CreatePlanForm>({
    name: '', slug: '', description: '', priceBdt: 0,
    durationDays: 30, maxRequestsPerDay: 50, maxRequestsPerMonth: 1500,
    features: [], isActive: true, isFeatured: false
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    const response = await api.getPlans();
    if (response.success && response.data) setPlans(response.data);
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const response = editingPlan
      ? await api.updatePlan(editingPlan.id, form)
      : await api.createPlan(form);
    if (response.success) {
      loadPlans();
      setShowModal(false);
      setEditingPlan(null);
      setForm({ name: '', slug: '', description: '', priceBdt: 0, durationDays: 30, maxRequestsPerDay: 50, maxRequestsPerMonth: 1500, features: [], isActive: true, isFeatured: false });
    } else {
      alert(response.error);
    }
  };

  const handleEdit = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setForm({
      name: plan.name, slug: plan.slug, description: plan.description || '',
      priceBdt: plan.priceBdt, durationDays: plan.durationDays,
      maxRequestsPerDay: plan.maxRequestsPerDay, maxRequestsPerMonth: plan.maxRequestsPerMonth,
      features: plan.features, isActive: plan.isActive, isFeatured: plan.isFeatured
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this plan?')) return;
    const response = await api.deletePlan(id);
    if (response.success) loadPlans();
    else alert(response.error);
  };

  const toggleFeature = (code: string) => {
    setForm(f => ({
      ...f,
      features: f.features.includes(code)
        ? f.features.filter(c => c !== code)
        : [...f.features, code]
    }));
  };

  const selectAllInCategory = (category: string) => {
    const categoryCodes = FEATURE_CATEGORIES[category].map(f => f.code);
    const allSelected = categoryCodes.every(code => form.features.includes(code));

    if (allSelected) {
      setForm(f => ({ ...f, features: f.features.filter(c => !categoryCodes.includes(c)) }));
    } else {
      setForm(f => ({ ...f, features: [...new Set([...f.features, ...categoryCodes])] }));
    }
  };

  const selectAll = () => {
    const allCodes = ALL_FEATURES.map(f => f.code);
    const allSelected = allCodes.every(code => form.features.includes(code));
    setForm(f => ({ ...f, features: allSelected ? [] : allCodes }));
  };

  const getFeatureInfo = (code: string) => ALL_FEATURES.find(f => f.code === code);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Subscription Plans</h1>
          <p className="text-neutral-500">Manage pricing and features</p>
        </div>
        <button onClick={() => { setEditingPlan(null); setForm({ name: '', slug: '', description: '', priceBdt: 0, durationDays: 30, maxRequestsPerDay: 50, maxRequestsPerMonth: 1500, features: [], isActive: true, isFeatured: false }); setShowModal(true); }} className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-medium transition-colors">+ Add Plan</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div key={plan.id} className={`bg-[#121212] border rounded-2xl p-6 ${plan.isFeatured ? 'border-emerald-500/50 ring-1 ring-emerald-500/20' : 'border-[#262626]'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">{plan.name}</h3>
              {plan.isFeatured && <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded font-medium">FEATURED</span>}
            </div>
            <p className="text-3xl font-bold text-white mb-2">৳{plan.priceBdt.toLocaleString()}<span className="text-neutral-500 text-sm font-normal">/mo</span></p>
            <p className="text-neutral-500 text-sm mb-4">{plan.description}</p>
            <div className="space-y-2 mb-4">
              <p className="text-neutral-400 text-sm">{plan.maxRequestsPerDay} daily / {plan.maxRequestsPerMonth.toLocaleString()} monthly</p>
              <div className="flex flex-wrap gap-1">
                {plan.features.slice(0, 4).map(code => {
                  const info = getFeatureInfo(code);
                  return (
                    <span key={code} className="px-2 py-0.5 bg-[#1a1a1a] text-neutral-400 text-xs rounded" title={info?.name}>
                      {info?.icon} {info?.name || code}
                    </span>
                  );
                })}
                {plan.features.length > 4 && (
                  <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 text-xs rounded">
                    +{plan.features.length - 4} more
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleEdit(plan)} className="flex-1 px-4 py-2 bg-[#1a1a1a] text-white rounded-lg text-sm hover:bg-[#262626] transition-colors">Edit</button>
              <button onClick={() => handleDelete(plan.id)} className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg text-sm hover:bg-red-500/20 transition-colors">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-[#121212] border border-[#262626] rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-[#262626]">
              <h2 className="text-xl font-bold text-white">{editingPlan ? 'Edit Plan' : 'Create Plan'}</h2>
              <p className="text-neutral-500 text-sm mt-1">Configure plan details and select features</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-neutral-400 uppercase tracking-wider">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-neutral-500 mb-1.5">Plan Name *</label>
                      <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Professional" required className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-emerald-500/50" />
                    </div>
                    <div>
                      <label className="block text-xs text-neutral-500 mb-1.5">Slug *</label>
                      <input type="text" value={form.slug} onChange={(e) => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))} placeholder="e.g. professional" required className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-emerald-500/50" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1.5">Description</label>
                    <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description of this plan" rows={2} className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-emerald-500/50 resize-none" />
                  </div>
                </div>

                {/* Pricing & Limits */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-neutral-400 uppercase tracking-wider">Pricing & Limits</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs text-neutral-500 mb-1.5">Price (BDT)</label>
                      <input type="number" value={form.priceBdt} onChange={(e) => setForm(f => ({ ...f, priceBdt: Number(e.target.value) }))} placeholder="999" className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50" />
                    </div>
                    <div>
                      <label className="block text-xs text-neutral-500 mb-1.5">Duration (days)</label>
                      <input type="number" value={form.durationDays} onChange={(e) => setForm(f => ({ ...f, durationDays: Number(e.target.value) }))} placeholder="30" className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50" />
                    </div>
                    <div>
                      <label className="block text-xs text-neutral-500 mb-1.5">Daily Limit</label>
                      <input type="number" value={form.maxRequestsPerDay} onChange={(e) => setForm(f => ({ ...f, maxRequestsPerDay: Number(e.target.value) }))} placeholder="50" className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50" />
                    </div>
                    <div>
                      <label className="block text-xs text-neutral-500 mb-1.5">Monthly Limit</label>
                      <input type="number" value={form.maxRequestsPerMonth} onChange={(e) => setForm(f => ({ ...f, maxRequestsPerMonth: Number(e.target.value) }))} placeholder="1500" className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50" />
                    </div>
                  </div>
                </div>

                {/* Feature Selector */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-neutral-400 uppercase tracking-wider">
                      Features <span className="text-emerald-400">({form.features.length} selected)</span>
                    </h3>
                    <button
                      type="button"
                      onClick={selectAll}
                      className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
                    >
                      {form.features.length === ALL_FEATURES.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>

                  <div className="space-y-4">
                    {Object.entries(FEATURE_CATEGORIES).map(([category, categoryFeatures]) => {
                      const selectedCount = categoryFeatures.filter(f => form.features.includes(f.code)).length;
                      const allSelected = selectedCount === categoryFeatures.length;

                      return (
                        <div key={category} className="bg-[#1a1a1a] border border-[#262626] rounded-xl overflow-hidden">
                          {/* Category Header */}
                          <div
                            className="flex items-center justify-between px-4 py-3 bg-[#0d0d0d] cursor-pointer hover:bg-[#151515] transition-colors"
                            onClick={() => selectAllInCategory(category)}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-white">{category}</span>
                              <span className="text-xs text-neutral-500">({selectedCount}/{categoryFeatures.length})</span>
                            </div>
                            <div className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${allSelected ? 'bg-emerald-500' : selectedCount > 0 ? 'bg-emerald-500/50' : 'bg-[#262626]'}`}>
                              {allSelected && <span className="text-white text-xs">✓</span>}
                              {!allSelected && selectedCount > 0 && <span className="text-white text-xs">−</span>}
                            </div>
                          </div>

                          {/* Features Grid */}
                          <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                            {categoryFeatures.map(feature => {
                              const isSelected = form.features.includes(feature.code);
                              return (
                                <div
                                  key={feature.code}
                                  onClick={() => toggleFeature(feature.code)}
                                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                    isSelected
                                      ? 'bg-emerald-500/10 border border-emerald-500/30'
                                      : 'bg-[#121212] border border-transparent hover:border-[#363636]'
                                  }`}
                                >
                                  <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                                    isSelected ? 'bg-emerald-500' : 'bg-[#262626]'
                                  }`}>
                                    {isSelected && <span className="text-white text-xs">✓</span>}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-lg">{feature.icon}</span>
                                      <span className={`text-sm font-medium truncate ${isSelected ? 'text-emerald-400' : 'text-white'}`}>
                                        {feature.name}
                                      </span>
                                    </div>
                                    <p className="text-xs text-neutral-500 truncate">{feature.description}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Options */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-neutral-400 uppercase tracking-wider">Options</h3>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${form.isActive ? 'bg-emerald-500' : 'bg-[#262626]'}`}>
                        {form.isActive && <span className="text-white text-xs">✓</span>}
                      </div>
                      <input type="checkbox" checked={form.isActive} onChange={(e) => setForm(f => ({ ...f, isActive: e.target.checked }))} className="sr-only" />
                      <span className="text-white text-sm group-hover:text-emerald-400 transition-colors">Active</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${form.isFeatured ? 'bg-emerald-500' : 'bg-[#262626]'}`}>
                        {form.isFeatured && <span className="text-white text-xs">✓</span>}
                      </div>
                      <input type="checkbox" checked={form.isFeatured} onChange={(e) => setForm(f => ({ ...f, isFeatured: e.target.checked }))} className="sr-only" />
                      <span className="text-white text-sm group-hover:text-emerald-400 transition-colors">Featured (highlighted on pricing page)</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-[#262626] bg-[#0d0d0d] flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-3 bg-[#1a1a1a] text-white rounded-xl font-medium hover:bg-[#262626] transition-colors">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-500 transition-colors">
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPlans;
